import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Mail, Send, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { facturacionApi } from '../../lib/api';
import { formatDateDMY } from '../../lib/utils';

const VARIABLES = ['{cliente}', '{unidad}', '{periodo}', '{monto}', '{vencimiento}', '{numero_factura}'];

export const EmailConfigButton = () => {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    facturacionApi.getEmailConfig().then(r => setCfg(r.data)).catch(() => toast.error('No se pudo cargar la configuración'));
  }, [open]);

  const set = (k) => (e) => setCfg(prev => ({ ...prev, [k]: e.target.value }));
  const guardar = async () => {
    setSaving(true);
    try {
      const r = await facturacionApi.saveEmailConfig({
        email_remitente: cfg.email_remitente, asunto: cfg.asunto, cuerpo: cfg.cuerpo, datos_bancarios: cfg.datos_bancarios,
      });
      setCfg(prev => ({ ...prev, ...r.data }));
      toast.success('Configuración de email guardada');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="h-8" onClick={() => setOpen(true)} data-testid="configurar-email-btn">
        <Mail className="w-4 h-4 mr-1.5" /> Configurar email
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="configurar-email-dialog">
          <DialogHeader>
            <DialogTitle>Email de envío de facturas</DialogTitle>
            <DialogDescription>
              Sale con el nombre <b>{cfg?.from_name || 'Global Citizen'}</b>. Las respuestas del cliente llegan al email de abajo.
            </DialogDescription>
          </DialogHeader>
          {!cfg ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
            <div className="space-y-3">
              <div>
                <Label>Email (las respuestas llegan acá)</Label>
                <Input value={cfg.email_remitente} onChange={set('email_remitente')} data-testid="email-config-remitente" />
              </div>
              <div>
                <Label>Asunto</Label>
                <Input value={cfg.asunto} onChange={set('asunto')} data-testid="email-config-asunto" />
              </div>
              <div>
                <Label>Cuerpo del email</Label>
                <Textarea rows={9} value={cfg.cuerpo} onChange={set('cuerpo')} data-testid="email-config-cuerpo" />
                <p className="text-xs text-slate-500 mt-1">Podés usar: {VARIABLES.join(' ')}. El PDF de la factura va adjunto.</p>
              </div>
              <div>
                <Label>Datos bancarios para el pago</Label>
                <Textarea rows={4} value={cfg.datos_bancarios || ''} onChange={set('datos_bancarios')}
                  placeholder="Banco, titular, CUIT, CBU, alias" data-testid="email-config-banco" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="email-config-cancelar">Cancelar</Button>
            <Button onClick={guardar} disabled={!cfg || saving} data-testid="email-config-guardar">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const EnviarFacturaCell = ({ item, edificioId, periodo, tienePdf, onSent }) => {
  const [sending, setSending] = useState(false);
  const enviada = item.factura_enviada_at;
  const enviar = async () => {
    if (enviada && !window.confirm(`Esta factura ya se envió el ${formatDateDMY(enviada.slice(0, 10))}. ¿Enviarla de nuevo?`)) return;
    setSending(true);
    try {
      const r = await facturacionApi.enviarFactura(edificioId, item.unidad_id, periodo);
      toast.success(`Factura enviada a ${r.data.enviada_a.join(', ')}`);
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar la factura');
    } finally {
      setSending(false);
    }
  };
  if (!tienePdf) return <span className="text-slate-300">-</span>;
  return (
    <div className="flex flex-col items-center gap-0.5">
      <Button size="sm" variant={enviada ? 'ghost' : 'outline'} className="h-7 text-xs" onClick={enviar} disabled={sending}
        title={enviada ? `Enviada a ${(item.factura_enviada_a || []).join(', ')}` : 'Enviar factura por email'}
        data-testid={`enviar-factura-btn-${item.unidad_id}`}>
        {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
        {enviada ? 'Reenviar' : 'Enviar'}
      </Button>
      {enviada && (
        <span className="text-[11px] text-emerald-700" data-testid={`factura-enviada-fecha-${item.unidad_id}`}>
          Enviada {formatDateDMY(enviada.slice(0, 10))}
        </span>
      )}
    </div>
  );
};
